import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";

const fields = [
  { key: "core_focus", label: "Core Focus", placeholder: "Our purpose + our niche in one sentence.", area: true },
  { key: "ten_year_target", label: "10-Year Target", placeholder: "Where will you be in a decade?", area: false },
  { key: "marketing_strategy", label: "Marketing Strategy", placeholder: "Target market + 3 uniques + proven process + guarantee.", area: true },
  { key: "three_year_picture", label: "3-Year Picture", placeholder: "What does the business look like in 3 years?", area: true },
  { key: "one_year_plan", label: "1-Year Plan", placeholder: "Revenue, profit, measurables, top 5 goals.", area: true },
];

export default function Vision() {
  const [v, setV] = useState(null);
  const [values, setValues] = useState([]);
  const [newVal, setNewVal] = useState("");

  useEffect(() => {
    api.get("/vision").then((r) => { setV(r.data); setValues(r.data.core_values || []); });
  }, []);

  if (!v) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;

  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });

  const save = async () => {
    const payload = { ...v, core_values: values };
    delete payload.updated_at; delete payload.id; delete payload.user_id;
    const { data } = await api.put("/vision", payload);
    setV(data);
    toast.success("V/TO saved.");
  };

  const addValue = () => {
    if (!newVal.trim()) return;
    setValues([...values, newVal.trim()]);
    setNewVal("");
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="mono text-xs uppercase tracking-widest text-neutral-500">Module · Vision</div>
      <h1 className="font-display text-4xl font-bold mt-2">V/TO — Vision / Traction Organizer</h1>
      <p className="text-neutral-600 mt-2">Answer once. Revisit yearly. This is the north star every module points to.</p>

      <div className="mt-8 space-y-6">
        <div className="border border-neutral-200 bg-white p-6">
          <div className="mono text-xs uppercase text-neutral-500 tracking-widest">Core Values</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {values.map((val, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-100 border border-neutral-300 text-sm rounded-sm" data-testid={`vision-value-${i}`}>
                {val}
                <button onClick={() => setValues(values.filter((_, j) => j !== i))} className="text-neutral-500 hover:text-red-600"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Input value={newVal} onChange={(e)=>setNewVal(e.target.value)} placeholder="Add a core value" onKeyDown={(e)=>e.key==="Enter"&&(e.preventDefault(),addValue())} className="rounded-sm" data-testid="vision-add-value-input" />
            <Button onClick={addValue} variant="outline" className="rounded-sm" data-testid="vision-add-value-btn"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>

        {fields.map((f) => (
          <div key={f.key} className="border border-neutral-200 bg-white p-6">
            <Label className="mono text-xs uppercase text-neutral-500 tracking-widest">{f.label}</Label>
            {f.area ? (
              <Textarea rows={3} value={v[f.key] || ""} onChange={set(f.key)} placeholder={f.placeholder} className="mt-3 rounded-sm" data-testid={`vision-${f.key}`} />
            ) : (
              <Input value={v[f.key] || ""} onChange={set(f.key)} placeholder={f.placeholder} className="mt-3 rounded-sm" data-testid={`vision-${f.key}`} />
            )}
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Button onClick={save} className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-sm btn-lift" data-testid="vision-save-btn">Save V/TO</Button>
      </div>
    </div>
  );
}
